import { useIndexStore } from '~/store';
import { useArticlesStore } from '~/store/articles';
import { useRecipesListWidgetStore } from '~/store/recipesListWidget';
import { isAnyRecipePage } from '~/utils/router';
import type { WidgetRecipe } from '~/types/typings';
import { type IWidgetsState, useWidgetsStore } from '~/store/widgets';

// Плагин для загрузки просмотров рецептов, которые отображаются в зонах виджетов
// для авторизованных в админке пользователей

export default defineNuxtPlugin((nuxtApp) => {
  if (!isAnyRecipePage(nuxtApp._route.name)) return;

  onNuxtReady(() => {
    const indexStore = useIndexStore();
    const articleStore = useArticlesStore();
    const recipesListStore = useRecipesListWidgetStore();

    let stopRecipesListAction: null | (() => void) = null;
    let stopIndexAction: null | (() => void) = null;

    watch(
      () => indexStore.isAuthenticatedInWordpress,
      (isAuthed) => {
        if (isAuthed) setupAuthenticatedListeners();
        else teardown();
      },
      { immediate: true },
    );

    function teardown() {
      stopRecipesListAction?.();
      stopRecipesListAction = null;

      stopIndexAction?.();
      stopIndexAction = null;
    }

    function fetchAllVisibleWidgetViews() {
      const customWidgetsData = getCustomWidgetsRecipesId();
      const relatedWidgetsData = recipesListStore.currentRelatedRecipes.map((item) => item.postId);
      const similarWidgetsData = recipesListStore.currentSimilarRecipes.map((item) => item.postId);

      const payload = [
        ...new Set([...customWidgetsData, ...relatedWidgetsData, ...similarWidgetsData]),
      ];
      articleStore.fetchViewsCount(payload);
    }

    function setupAuthenticatedListeners() {
      teardown();

      fetchAllVisibleWidgetViews();

      stopRecipesListAction = recipesListStore.$onAction((action) => {
        if (action.name === 'addSimilarRecipes' || action.name === 'addRelatedRecipes') {
          const list = action.args[0] as WidgetRecipe[];
          const ids = list.map((item) => item.postId);
          articleStore.fetchViewsCount(ids);
        }
      });

      stopIndexAction = indexStore.$onAction((action) => {
        if (action.name === 'updateWordpressAuthentication') {
          const isLoggedIn = action.args[0] as boolean;
          if (!isLoggedIn) teardown();
        }
      });
    }
  });
});

// Получение списка id виджетов с рецептами с типом custom
function getCustomWidgetsRecipesId(): number[] {
  const ids: number[] = [];
  const widgetsState = useWidgetsStore().$state;

  const keys = Object.keys(widgetsState);
  keys.forEach((key) => {
    widgetsState[key as keyof IWidgetsState]?.forEach((widget: TWidget | WidgetInRecipes) => {
      if (widget?.type === 'recipes-list' && 'list' in widget && widget.list === 'custom') {
        const currentIds = widget?.recipes?.reduce((acc: number[], item) => {
          return [...acc, item.postId];
        }, []);
        if (currentIds) {
          ids.push(...currentIds);
        }
      }
    });
  });

  return ids;
}
